import { apiRequest, apiRequestRaw } from '../../../shared/services/apiClient';
import { Message } from '../types/Conversation';
import { normalizeMessage } from './conversationService';

export type ConversationVoiceMode = 'PushToTalk' | 'Duplex';
export type ConversationVoiceSpeechDelivery = 'inline' | 'stream' | 'none';

export interface ConversationVoiceEndpointing {
  silenceMs: number;
  minUtteranceMs: number;
  maxUtteranceSeconds: number;
  vadRmsThreshold: number;
  bargeInRmsThreshold: number;
  bargeInHoldoffMs: number;
  minTranscriptChars: number;
}

export type ConversationVoiceTransport = 'http' | 'realtime';

export interface ConversationVoiceCapabilities {
  enabled: boolean;
  available: boolean;
  mode: ConversationVoiceMode;
  provider: string;
  voice: string;
  audioContentType: string;
  maxTurnAudioBytes: number;
  supportedInputMimeTypes: string[];
  bargeInEnabled: boolean;
  streamSpeech: boolean;
  partialTranscripts: boolean;
  speechDelivery: ConversationVoiceSpeechDelivery;
  streamAudioContentType: string;
  streamSampleRate: number;
  endpointing: ConversationVoiceEndpointing;
  realtimeAvailable: boolean;
  transport: ConversationVoiceTransport;
  realtimePathTemplate?: string;
  maxVoiceTokens?: number | null;
  voiceTokensUsed: number;
  voiceTokensRemaining?: number | null;
  voiceTokenQuotaExceeded: boolean;
  aiTokenQuotaExceeded: boolean;
}

export interface ConversationVoiceSession {
  sessionId: string;
  conversationId: string;
  mode: ConversationVoiceMode;
  voice: string;
  speechDelivery: ConversationVoiceSpeechDelivery;
  expiresAtUtc: string;
  transport: ConversationVoiceTransport;
  realtimePath?: string;
}

export interface ConversationVoiceTurnResult {
  sessionId: string;
  transcript: string;
  userMessage: Message | null;
  assistantMessage: Message | null;
  audioBase64?: string;
  audioContentType?: string;
  speechDelivery: ConversationVoiceSpeechDelivery;
  skipped: boolean;
}

export interface ConversationVoiceSpeechStream {
  response: Response;
  contentType: string;
  format: string;
  sampleRate: number;
}

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !payload.sessionId && !payload.enabled) {
    return payload.data;
  }
  return payload;
};

const normalizeMode = (value: unknown): ConversationVoiceMode =>
  String(value ?? '').trim().toLowerCase() === 'pushtotalk' ? 'PushToTalk' : 'Duplex';

const normalizeSpeechDelivery = (value: unknown): ConversationVoiceSpeechDelivery => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'inline' || normalized === 'stream' || normalized === 'none') {
    return normalized;
  }
  return 'inline';
};

const numberOr = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeVoiceEndpointing = (value: any): ConversationVoiceEndpointing => ({
  silenceMs: numberOr(value?.silenceMs, 650),
  minUtteranceMs: numberOr(value?.minUtteranceMs, 280),
  maxUtteranceSeconds: numberOr(value?.maxUtteranceSeconds, 20),
  vadRmsThreshold: numberOr(value?.vadRmsThreshold, 0.015),
  bargeInRmsThreshold: numberOr(value?.bargeInRmsThreshold, 0.045),
  bargeInHoldoffMs: numberOr(value?.bargeInHoldoffMs, 400),
  minTranscriptChars: numberOr(value?.minTranscriptChars, 2),
});

export const normalizeVoiceCapabilities = (value: any): ConversationVoiceCapabilities => {
  const mode = normalizeMode(value?.mode);
  const streamSpeech = value?.streamSpeech !== false;
  return {
    enabled: Boolean(value?.enabled),
    available: Boolean(value?.available),
    mode,
    provider: typeof value?.provider === 'string' ? value.provider.trim() : 'none',
    voice: typeof value?.voice === 'string' && value.voice.trim() ? value.voice.trim() : 'alloy',
    audioContentType: typeof value?.audioContentType === 'string' && value.audioContentType.trim()
      ? value.audioContentType.trim()
      : 'audio/mpeg',
    maxTurnAudioBytes: numberOr(value?.maxTurnAudioBytes, 8 * 1024 * 1024) || 8 * 1024 * 1024,
    supportedInputMimeTypes: Array.isArray(value?.supportedInputMimeTypes)
      ? value.supportedInputMimeTypes.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
      : ['audio/webm'],
    bargeInEnabled: value?.bargeInEnabled !== false,
    streamSpeech,
    partialTranscripts: Boolean(value?.partialTranscripts),
    speechDelivery: value?.speechDelivery
      ? normalizeSpeechDelivery(value.speechDelivery)
      : (mode === 'Duplex' && streamSpeech ? 'stream' : 'inline'),
    streamAudioContentType: typeof value?.streamAudioContentType === 'string' && value.streamAudioContentType.trim()
      ? value.streamAudioContentType.trim()
      : 'audio/pcm',
    streamSampleRate: numberOr(value?.streamSampleRate, 24000) || 24000,
    endpointing: normalizeVoiceEndpointing(value?.endpointing),
    realtimeAvailable: Boolean(value?.realtimeAvailable),
    transport: String(value?.transport ?? '').trim().toLowerCase() === 'realtime' ? 'realtime' : 'http',
    realtimePathTemplate: typeof value?.realtimePathTemplate === 'string' && value.realtimePathTemplate.trim()
      ? value.realtimePathTemplate.trim()
      : undefined,
    maxVoiceTokens: value?.maxVoiceTokens == null || Number(value.maxVoiceTokens) <= 0
      ? null
      : Math.floor(Number(value.maxVoiceTokens)),
    voiceTokensUsed: Math.max(0, Math.floor(numberOr(value?.voiceTokensUsed, 0))),
    voiceTokensRemaining: value?.voiceTokensRemaining == null ? null : Math.max(0, Math.floor(numberOr(value.voiceTokensRemaining, 0))),
    voiceTokenQuotaExceeded: Boolean(value?.voiceTokenQuotaExceeded),
    aiTokenQuotaExceeded: Boolean(value?.aiTokenQuotaExceeded),
  };
};

export const normalizeVoiceSession = (value: any): ConversationVoiceSession => ({
  sessionId: String(value?.sessionId ?? ''),
  conversationId: String(value?.conversationId ?? ''),
  mode: normalizeMode(value?.mode),
  voice: typeof value?.voice === 'string' && value.voice.trim() ? value.voice.trim() : 'alloy',
  speechDelivery: value?.speechDelivery
    ? normalizeSpeechDelivery(value.speechDelivery)
    : (normalizeMode(value?.mode) === 'Duplex' ? 'stream' : 'inline'),
  expiresAtUtc: String(value?.expiresAtUtc ?? ''),
  transport: String(value?.transport ?? '').trim().toLowerCase() === 'realtime' ? 'realtime' : 'http',
  realtimePath: typeof value?.realtimePath === 'string' && value.realtimePath.trim()
    ? value.realtimePath.trim()
    : undefined,
});

export const normalizeVoiceTurn = (value: any): ConversationVoiceTurnResult => ({
  sessionId: String(value?.sessionId ?? ''),
  transcript: String(value?.transcript ?? ''),
  userMessage: value?.userMessage ? normalizeMessage(value.userMessage) : null,
  assistantMessage: value?.assistantMessage ? normalizeMessage(value.assistantMessage) : null,
  audioBase64: typeof value?.audioBase64 === 'string' && value.audioBase64.trim() ? value.audioBase64.trim() : undefined,
  audioContentType: typeof value?.audioContentType === 'string' && value.audioContentType.trim()
    ? value.audioContentType.trim()
    : undefined,
  speechDelivery: normalizeSpeechDelivery(value?.speechDelivery),
  skipped: Boolean(value?.skipped),
});

export const getConversationVoiceCapabilities = async (
  conversationId: string,
): Promise<ConversationVoiceCapabilities> => {
  const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/voice/capabilities`);
  return normalizeVoiceCapabilities(pickSingle(payload));
};

export const startConversationVoiceSession = async (
  conversationId: string,
): Promise<ConversationVoiceSession> => {
  const payload = await apiRequest<any>(`/api/v1/conversations/${conversationId}/voice/sessions`, {
    method: 'POST',
  });
  const session = normalizeVoiceSession(pickSingle(payload));
  if (!session.sessionId) {
    throw new Error('Voice session could not be started.');
  }
  return session;
};

export const submitConversationVoiceTurn = async (
  conversationId: string,
  sessionId: string,
  audio: Blob,
  fileName = 'voice-turn.webm',
  options?: {
    speechDelivery?: ConversationVoiceSpeechDelivery;
    signal?: AbortSignal;
  },
): Promise<ConversationVoiceTurnResult> => {
  const formData = new FormData();
  const file = audio instanceof File ? audio : new File([audio], fileName, { type: audio.type || 'audio/webm' });
  formData.append('audio', file, file.name);
  const speech = options?.speechDelivery ? `?speech=${encodeURIComponent(options.speechDelivery)}` : '';
  const payload = await apiRequest<any>(
    `/api/v1/conversations/${conversationId}/voice/sessions/${sessionId}/turns${speech}`,
    {
      method: 'POST',
      body: formData,
      signal: options?.signal,
    },
  );
  return normalizeVoiceTurn(pickSingle(payload));
};

export const streamConversationVoiceSpeech = async (
  conversationId: string,
  sessionId: string,
  options?: {
    text?: string;
    assistantMessageId?: string;
    signal?: AbortSignal;
  },
): Promise<ConversationVoiceSpeechStream> => {
  const body = options?.text || options?.assistantMessageId
    ? JSON.stringify({
      text: options.text,
      assistantMessageId: options.assistantMessageId,
    })
    : JSON.stringify({});
  const response = await apiRequestRaw(
    `/api/v1/conversations/${conversationId}/voice/sessions/${sessionId}/speech`,
    {
      method: 'POST',
      body,
      signal: options?.signal,
    },
  );
  const format = (response.headers.get('X-Voice-Format') || '').trim().toLowerCase();
  const sampleRate = Number(response.headers.get('X-Voice-Sample-Rate'));
  const contentType = (response.headers.get('Content-Type') || '').split(';')[0].trim();
  return {
    response,
    contentType: contentType || (format === 'pcm' ? 'audio/pcm' : 'audio/mpeg'),
    format: format || (contentType.includes('pcm') ? 'pcm' : 'mp3'),
    sampleRate: Number.isFinite(sampleRate) && sampleRate > 0 ? sampleRate : 24000,
  };
};

export const interruptConversationVoiceSession = async (
  conversationId: string,
  sessionId: string,
): Promise<void> => {
  await apiRequest<any>(`/api/v1/conversations/${conversationId}/voice/sessions/${sessionId}/interrupt`, {
    method: 'POST',
  });
};

export const endConversationVoiceSession = async (
  conversationId: string,
  sessionId: string,
): Promise<void> => {
  await apiRequest<any>(`/api/v1/conversations/${conversationId}/voice/sessions/${sessionId}`, {
    method: 'DELETE',
  });
};
